import create from "zustand";

import type { AskPerformYardState } from "./types";

import {
	buildGreetingMessage,
	clearPersistedInterviewerState,
	clearStoredConversationId,
	initialDocumentModalState,
	resetSessionId,
} from "./helpers";
import { createChatSlice } from "./slices/chat-slice";
import { createCompetencySlice } from "./slices/competency-slice";
import { createConversationSlice } from "./slices/conversation-slice";
import { createDocumentModalSlice } from "./slices/document-modal-slice";
import { createFeatureSlice } from "./slices/feature-slice";
import { createFormAuthoringCoachSlice } from "./slices/form-authoring-coach-slice";
import { createFormBuildingCoachSlice } from "./slices/form-building-coach-slice";
import { createFormQuestionSlice } from "./slices/form-question-slice";
import { createFormSlice } from "./slices/form-slice";
import { createGoalCoachSlice } from "./slices/goal-coach-slice";
import { createGoalSlice } from "./slices/goal-slice";
import { createReportSlice } from "./slices/report-slice";
import { createScrollSlice } from "./slices/scroll-slice";
import { createUiSlice } from "./slices/ui-slice";
import { drainPendingChunks, setStoreRef } from "./streaming-buffer";

// Interviews don't resume across reloads — clear any persisted harness/notes on load.
clearPersistedInterviewerState();

export const useAskPerformYardStore = create<AskPerformYardState>((set, get) => ({
	...createUiSlice(set),
	...createChatSlice(set),
	...createConversationSlice(set),
	...createFeatureSlice(set),
	...createGoalSlice(set),
	...createGoalCoachSlice(set, get),
	...createFormSlice(set),
	...createFormQuestionSlice(set),
	...createFormBuildingCoachSlice(set, get),
	...createFormAuthoringCoachSlice(set, get),
	...createDocumentModalSlice(set),
	...createScrollSlice(set),
	...createCompetencySlice(set, get),
	...createReportSlice(set),

	// Reset chat state (note: does not reset UI state like isOpen/isExpanded)
	resetChat: () => {
		drainPendingChunks();
		clearPersistedInterviewerState();
		// New Chat is the explicit conversation boundary (PY-13559): drop the
		// pointer so the next turn creates a new conversation rather than
		// appending to the one the user just left.
		clearStoredConversationId();
		set((state) => ({
			sessionId: resetSessionId(),
			conversationId: null,
			conversationTitle: null,
			isLoadingConversation: false,
			messages: [
				buildGreetingMessage({
					askHrEnabled: state.cachedAskHrEnabled,
					goalCreationEnabled: state.cachedGoalCreationEnabled,
					formCreationEnabled: state.cachedFormCreationEnabled,
					competencyInterviewerEnabled: state.cachedCompetencyInterviewerEnabled,
					reportBuilderEnabled: state.cachedReportBuilderEnabled,
					isOnFormBuilderPage: state.cachedIsOnFormBuilderPage,
					isOnGoalCreationPage: state.cachedIsOnGoalCreationPage,
					goalCoachingEnabled: state.cachedGoalCoachingEnabled,
					formBuildingCoachEnabled: state.cachedFormBuildingCoachEnabled,
					isOnCompetencyPage: state.cachedIsOnCompetencyPage,
				}),
			],
			status: "idle",
			streamStage: null,
			streamMessage: null,
			error: null,
			inputValue: "",
			hasNoDocuments: false,
			goalSuggestionStates: [],
			goalImprovementStates: [],
			creatingSuggestionIds: new Set<string>(),
			isCreatingAll: false,
			isGoalEditPanelOpen: false,
			activeEditMessageId: null,
			formSuggestionStates: [],
			creatingFormIds: new Set<string>(),
			formQuestionSuggestionStates: [],
			formImprovementStates: [],
			responseImprovementStates: [],
			responseImprovementError: null,
			aiImprovedAnswerIndexes: [],
			documentModal: initialDocumentModalState,
			competencyFramework: null,
			isCompetencyEditPanelOpen: false,
			activeInterviewerHarness: null,
			reportBuilder: null,
		}));
	},
}));

// Wire up the lazy store reference for the streaming buffer
setStoreRef(useAskPerformYardStore);
